import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
	Timestamp,
	addDoc,
	collection,
	deleteDoc,
	doc,
	getDoc,
	getDocs,
	query,
	updateDoc,
	where,
} from 'firebase/firestore';
import { chunk } from 'lodash';
import BASE_QUERY from '@/features/baseQuery';
import { firestore, toBrokerGroupRefs } from '@/utils/firebase';
import { getActingUser } from '@/features/user/userApi';

const UNLAYER_DOCUMENT_TEMPLATES_COLLECTION = 'unlayerDocumentTemplates';
/**
 * Firestore index notes for `unlayerDocumentTemplates`:
 * - Current list query uses `where('brokerGroupIds', 'array-contains-any', [...])` with client-side sorting.
 *   This does not require a composite index.
 * - If list queries move to server-side ordering by update time, add a composite index:
 *   `brokerGroupIds` (array-contains) + `lastUpdated` (descending).
 */

const toUserRef = (userId) => doc(firestore, 'user', String(userId));

const asMillis = (value) => value?.toMillis?.() ?? 0;

const stripUndefinedDeep = (value) => {
	if (Array.isArray(value)) {
		return value.map((item) => stripUndefinedDeep(item));
	}

	if (value && typeof value === 'object') {
		return Object.entries(value).reduce((acc, [key, nestedValue]) => {
			if (nestedValue === undefined) return acc;
			acc[key] = stripUndefinedDeep(nestedValue);
			return acc;
		}, {});
	}

	return value;
};

const normalizeDesignJson = (designJson) => {
	if (typeof designJson === 'string') {
		try {
			return stripUndefinedDeep(JSON.parse(designJson));
		} catch (error) {
			throw new Error('Invalid design payload');
		}
	}

	if (designJson && typeof designJson === 'object') {
		return stripUndefinedDeep(designJson);
	}

	throw new Error('Invalid design payload');
};

const serializeDesignJsonForStorage = (designJson) => {
	const normalized = normalizeDesignJson(designJson);
	try {
		return JSON.stringify(normalized);
	} catch (error) {
		throw new Error('Invalid design payload');
	}
};

const parseDesignJsonFromStorage = (storedValue) => {
	if (typeof storedValue === 'string') {
		return normalizeDesignJson(storedValue);
	}
	if (storedValue && typeof storedValue === 'object') {
		return stripUndefinedDeep(storedValue);
	}
	return null;
};

const toError = (error, fallbackMessage) => ({
	status: error?.code ?? 'CUSTOM_ERROR',
	data: {
		message:
			typeof error?.message === 'string' && error.message.length > 0
				? error.message
				: fallbackMessage,
	},
});

const hasBrokerGroupAccess = (brokerGroupRefs, brokerGroupIds) => {
	const allowedIds = new Set((brokerGroupIds ?? []).map((id) => String(id)));
	return (brokerGroupRefs ?? []).some((ref) => ref?.id && allowedIds.has(String(ref.id)));
};

const canAccessScopedDocument = (data, { userId, brokerGroupIds = [] }) => {
	const createdById = data?.createdBy?.id ? String(data.createdBy.id) : null;
	if (createdById && userId && createdById === String(userId)) {
		return true;
	}
	return hasBrokerGroupAccess(data?.brokerGroups, brokerGroupIds);
};

const serializeTemplateSummary = (templateDoc) => {
	const data = templateDoc?.data?.() ?? {};
	return {
		id: templateDoc.id,
		name: data?.name ?? '',
		ownerDocId: data?.ownerDocId ?? null,
		createdById: data?.createdBy?.id ?? null,
		lastUpdated: asMillis(data?.lastUpdated ?? data?.updatedAt),
		createdWhen: asMillis(data?.createdWhen ?? data?.createdAt),
	};
};

const serializeTemplateDetail = (templateDoc) => {
	const data = templateDoc?.data?.() ?? {};
	return {
		...serializeTemplateSummary(templateDoc),
		ownerDocId: data?.ownerDocId ?? null,
		createdById: data?.createdBy?.id ?? null,
		brokerGroupIds: (data?.brokerGroups ?? []).map((ref) => ref?.id).filter(Boolean),
		designJson: parseDesignJsonFromStorage(data?.designJson),
	};
};

export const unlayerDocumentTemplatesApi = createApi({
	reducerPath: 'unlayerDocumentTemplatesApi',
	tagTypes: ['UNLAYER_DOCUMENT_TEMPLATES', 'UNLAYER_DOCUMENT_TEMPLATE'],
	baseQuery: fetchBaseQuery(BASE_QUERY),
	endpoints: (builder) => ({
		getUnlayerDocumentTemplateSummaries: builder.query({
			providesTags: ['UNLAYER_DOCUMENT_TEMPLATES'],
			queryFn: async ({ brokerGroupIds = [] }) => {
				try {
					const brokerGroupRefs = toBrokerGroupRefs(brokerGroupIds);
					if (brokerGroupRefs.length === 0) {
						return { data: [] };
					}

					const collectionRef = collection(
						firestore,
						UNLAYER_DOCUMENT_TEMPLATES_COLLECTION,
					);
					const brokerGroupChunks = chunk(brokerGroupIds, 30);
					const templateMap = new Map();

					for (const brokerGroupChunk of brokerGroupChunks) {
						const snapshot = await getDocs(
							query(
								collectionRef,
								where('brokerGroupIds', 'array-contains-any', brokerGroupChunk),
							),
						);
						snapshot.docs.forEach((templateDoc) => {
							templateMap.set(templateDoc.id, serializeTemplateSummary(templateDoc));
						});
					}

					const summaries = Array.from(templateMap.values()).sort(
						(a, b) => b.lastUpdated - a.lastUpdated,
					);
					return { data: summaries };
				} catch (error) {
					console.error('Error fetching Unlayer document template summaries', error);
					return { error: toError(error, 'Unable to load document templates') };
				}
			},
		}),
		getUnlayerDocumentTemplateDetail: builder.query({
			providesTags: (result, error, { id }) => [{ type: 'UNLAYER_DOCUMENT_TEMPLATE', id }],
			queryFn: async ({ id, userId, brokerGroupIds = [] }) => {
				try {
					if (!id) {
						throw new Error('Template id is required');
					}
					const templateRef = doc(firestore, UNLAYER_DOCUMENT_TEMPLATES_COLLECTION, id);
					const snapshot = await getDoc(templateRef);
					if (!snapshot.exists()) {
						throw new Error('Template not found');
					}
					const data = snapshot.data();
					if (!canAccessScopedDocument(data, { userId, brokerGroupIds })) {
						throw new Error('Access denied for this template');
					}
					return {
						data: serializeTemplateDetail(snapshot),
					};
				} catch (error) {
					console.error('Error fetching Unlayer document template detail', error);
					return { error: toError(error, 'Unable to load document template') };
				}
			},
		}),
		createUnlayerDocumentTemplate: builder.mutation({
			invalidatesTags: ['UNLAYER_DOCUMENT_TEMPLATES'],
			queryFn: async ({ name, designJson, brokerGroupIds = [] }, { dispatch, getState }) => {
				try {
					if (!name) {
						throw new Error('Missing required template fields');
					}

					const performer = await getActingUser(dispatch, getState);
					const userId = performer.userId;

					const now = Timestamp.now();
					const payload = {
						name: String(name).trim(),
						designJson: serializeDesignJsonForStorage(designJson),
						createdAt: now,
						updatedAt: now,
						createdWhen: now,
						lastUpdated: now,
						createdBy: toUserRef(userId),
						brokerGroups: toBrokerGroupRefs(brokerGroupIds),
						// Plain ids: what the list query and the Firestore rules check.
						brokerGroupIds,
						ownerDocId: userId,
					};

					const result = await addDoc(
						collection(firestore, UNLAYER_DOCUMENT_TEMPLATES_COLLECTION),
						payload,
					);

					return {
						data: {
							id: result.id,
							name: payload.name,
							designJson: normalizeDesignJson(designJson),
							createdWhen: now.toMillis(),
							lastUpdated: now.toMillis(),
							ownerDocId: userId,
							createdById: String(userId),
							brokerGroupIds: (brokerGroupIds ?? []).map(String),
						},
					};
				} catch (error) {
					console.error('Error creating Unlayer document template', error);
					return { error: toError(error, 'Unable to create document template') };
				}
			},
		}),
		updateUnlayerDocumentTemplate: builder.mutation({
			invalidatesTags: (result, error, { id }) => [
				{ type: 'UNLAYER_DOCUMENT_TEMPLATE', id },
				'UNLAYER_DOCUMENT_TEMPLATES',
			],
			queryFn: async (
				{ id, name, designJson, brokerGroupIds = [] },
				{ dispatch, getState },
			) => {
				try {
					if (!id || !name) {
						throw new Error('Missing required template fields');
					}

					const performer = await getActingUser(dispatch, getState);
					const userId = performer.userId;

					const templateRef = doc(firestore, UNLAYER_DOCUMENT_TEMPLATES_COLLECTION, id);
					const snapshot = await getDoc(templateRef);
					if (!snapshot.exists()) {
						throw new Error('Template not found');
					}
					const existingData = snapshot.data();
					if (!canAccessScopedDocument(existingData, { userId, brokerGroupIds })) {
						throw new Error('Access denied for this template');
					}

					const lastUpdated = Timestamp.now();
					await updateDoc(templateRef, {
						name: String(name).trim(),
						designJson: serializeDesignJsonForStorage(designJson),
						lastUpdated,
						updatedAt: lastUpdated,
						createdBy: toUserRef(userId),
						brokerGroups: toBrokerGroupRefs(brokerGroupIds),
						brokerGroupIds,
						ownerDocId: userId,
					});

					return {
						data: {
							id,
							name: String(name).trim(),
							designJson: normalizeDesignJson(designJson),
							lastUpdated: lastUpdated.toMillis(),
							ownerDocId: userId,
							createdById: String(userId),
							brokerGroupIds: (brokerGroupIds ?? []).map(String),
						},
					};
				} catch (error) {
					console.error('Error updating Unlayer document template', error);
					return { error: toError(error, 'Unable to update document template') };
				}
			},
		}),
		deleteUnlayerDocumentTemplate: builder.mutation({
			invalidatesTags: (result, error, { id }) => [
				{ type: 'UNLAYER_DOCUMENT_TEMPLATE', id },
				'UNLAYER_DOCUMENT_TEMPLATES',
			],
			queryFn: async ({ id, userId, brokerGroupIds = [] }) => {
				try {
					if (!id) {
						throw new Error('Template id is required');
					}
					const templateRef = doc(firestore, UNLAYER_DOCUMENT_TEMPLATES_COLLECTION, id);
					const snapshot = await getDoc(templateRef);
					if (!snapshot.exists()) {
						throw new Error('Template not found');
					}
					const existingData = snapshot.data();
					if (!canAccessScopedDocument(existingData, { userId, brokerGroupIds })) {
						throw new Error('Access denied for this template');
					}
					await deleteDoc(templateRef);
					return { data: { id } };
				} catch (error) {
					console.error('Error deleting Unlayer document template', error);
					return { error: toError(error, 'Unable to delete document template') };
				}
			},
		}),
	}),
});

export const {
	useLazyGetUnlayerDocumentTemplateSummariesQuery,
	useLazyGetUnlayerDocumentTemplateDetailQuery,
	useCreateUnlayerDocumentTemplateMutation,
	useUpdateUnlayerDocumentTemplateMutation,
	useDeleteUnlayerDocumentTemplateMutation,
} = unlayerDocumentTemplatesApi;
